import React, { useRef, useState } from 'react';
import classnames from 'classnames';
import * as mobxReact from 'mobx-react';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { CommonModal } from '../../../../global/modal/common/common-modal';
import { MaterialSpinner } from '../../../../global/spinner/material-spinner';
import DefaultButton from '../../../../global/default-button/default-button';
import OECheckbox from '../../../../global/oe-checkbox/oe-checkbox';
import { debounce } from '../../../../../infrastructure/utils/debounce.utils';
import { Tooltip } from '../../../../global/tooltip/tooltip';
import { EMPTY_VALUE, toNumber } from './salesforce-order-display.mapper';
import { IManageProductsSelection, isQuantityInput, ManageProductsViewModel, selectionLineTotal } from './manage-products.viewmodel';
import { APPLY_TEXT, CANCEL_TEXT } from '../../../../../constants/common-labels.constants';
import {
    MANAGE_PRODUCTS_ADD_LINE_ITEM_TEXT,
    MANAGE_PRODUCTS_CATALOGUE_EMPTY_TEXT,
    MANAGE_PRODUCTS_ERROR_TEXT,
    MANAGE_PRODUCTS_LOADING_TEXT,
    MANAGE_PRODUCTS_MODAL_TITLE,
    MANAGE_PRODUCTS_NOTE_REQUIRED_TEXT,
    MANAGE_PRODUCTS_UNIT_PRICE_INVALID_TEXT,
    MANAGE_PRODUCTS_RETRY_TEXT,
    MANAGE_PRODUCTS_SEARCH_PLACEHOLDER,
    MANAGE_PRODUCTS_SELECTED_EMPTY_TEXT,
    FIELD_CURRENCY_LABEL,
    FIELD_PRICE_BOOK_LABEL,
    PRODUCTS_TOTAL_LABEL,
    PRODUCT_COLUMNS,
} from './salesforce-order.texts';
import './manage-products.scss';

const SEARCH_DEBOUNCE_MS = 250;

interface IProps {
    vm: ManageProductsViewModel;
    isOpen: boolean;
    eventName: string;
    eventWhenLines: string[];
}

const Money = (props: { value: string | number | null }) => {
    const num = toNumber(props.value);
    if (num == null) {
        return <>{EMPTY_VALUE}</>;
    }
    return <FormattedNumber value={num} minimumFractionDigits={2} maximumFractionDigits={2} />;
};

const formatAmount = (value: number | null): string =>
    (value == null ? '' : value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

const AmountInput = (props: { value: number | null; testId: string; invalid?: boolean; onChange: (raw: string) => void }) => {
    const [draft, setDraft] = useState<string | null>(null);
    const display = draft != null ? draft : formatAmount(props.value);
    return (
        <input
            type='text'
            inputMode='decimal'
            className={classnames('manage-products__input', 'manage-products__input--number', {
                'manage-products__input--error': props.invalid,
            })}
            data-test-id={props.testId}
            value={display}
            onFocus={() => setDraft(props.value ? String(props.value) : '')}
            onChange={event => { setDraft(event.target.value); props.onChange(event.target.value); }}
            onBlur={() => setDraft(null)}
        />
    );
};

const formatQuantity = (value: number): string => (value === 0 ? '' : String(value));

const QuantityInput = (props: { value: number; onChange: (raw: string) => void; onBlur: () => void }) => {
    const [draft, setDraft] = useState<string | null>(null);
    return (
        <input
            type='text'
            inputMode='decimal'
            className='manage-products__input manage-products__input--qty'
            data-test-id='manage-products-qty'
            value={draft != null ? draft : formatQuantity(props.value)}
            placeholder='0'
            onFocus={() => setDraft(formatQuantity(props.value))}
            onChange={event => {
                if (isQuantityInput(event.target.value)) {
                    setDraft(event.target.value);
                    props.onChange(event.target.value);
                }
            }}
            onBlur={() => {
                setDraft(null);
                props.onBlur();
            }}
        />
    );
};

const SelectedSummary = mobxReact.observer((props: { vm: ManageProductsViewModel }) => {
    const { vm } = props;
    return (
        <div className='manage-products__summary' data-test-id='manage-products-summary'>
            {vm.orderedProducts.length === 0 ? (
                <p className='manage-products__summary-empty'>
                    <FormattedMessage {...MANAGE_PRODUCTS_SELECTED_EMPTY_TEXT} />
                </p>
            ) : (
                <table className='manage-products__summary-table'>
                    <thead>
                        <tr>
                            <th className='manage-products__summary-col-product'>
                                <FormattedMessage {...PRODUCT_COLUMNS.product} />
                            </th>
                            <th className='manage-products__summary-col-qty'>
                                <FormattedMessage {...PRODUCT_COLUMNS.quantity} />
                            </th>
                            <th className='manage-products__summary-col-total'>
                                <FormattedMessage {...PRODUCT_COLUMNS.total} />
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {vm.orderedProducts.map(selection => (
                            <tr key={selection.rowId}>
                                <td className='manage-products__summary-col-product'>
                                    <span className='manage-products__summary-name'>{selection.productName}</span>
                                </td>
                                <td className='manage-products__summary-col-qty'>{selection.quantity}</td>
                                <td className='manage-products__summary-col-total'>
                                    <Money value={selectionLineTotal(selection)} />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
});

const LeftPanel = mobxReact.observer((props: { vm: ManageProductsViewModel; eventName: string; eventWhenLines: string[] }) => {
    const { vm, eventName, eventWhenLines } = props;
    return (
        <aside className='manage-products__left' data-test-id='manage-products-left-panel'>
            <div className='manage-products__event'>
                {eventName && <h3 className='manage-products__event-name'>{eventName}</h3>}
                {eventWhenLines.map((line, index) => (
                    <span className='manage-products__event-when' key={index}>{line}</span>
                ))}
            </div>
            <dl className='manage-products__meta'>
                <div className='manage-products__meta-row'>
                    <dt><FormattedMessage {...FIELD_PRICE_BOOK_LABEL} /></dt>
                    {/* Read-only box — populated from the selected Contract; wraps a long name onto two lines. */}
                    <dd>
                        <div className='manage-products__field-box' data-test-id='manage-products-price-book'>
                            <span className='manage-products__field-text'>{vm.priceBookName || EMPTY_VALUE}</span>
                        </div>
                    </dd>
                </div>
                <div className='manage-products__meta-row'>
                    <dt><FormattedMessage {...FIELD_CURRENCY_LABEL} /></dt>
                    {/* Read-only — the order's currency is fixed (populated from the contract). */}
                    <dd>
                        <div className='manage-products__field-box' data-test-id='manage-products-currency'>
                            <span className='manage-products__field-value'>{vm.currency || EMPTY_VALUE}</span>
                        </div>
                    </dd>
                </div>
            </dl>
            <div className='manage-products__grand-total' data-test-id='manage-products-total'>
                <span><FormattedMessage {...PRODUCTS_TOTAL_LABEL} /></span>
                <span>{vm.currency ? `${vm.currency} ` : ''}<Money value={vm.total} /></span>
            </div>
            <SelectedSummary vm={vm} />
        </aside>
    );
});


const AddLineItemCell = (props: { vm: ManageProductsViewModel; row: IManageProductsSelection }) => {
    const intl = useIntl();
    const label = intl.formatMessage(MANAGE_PRODUCTS_ADD_LINE_ITEM_TEXT);
    if (!props.row.checked) {
        return <td className='manage-products__col-add' />;
    }
    return (
        <td className='manage-products__col-add'>
            <Tooltip content={label} className='manage-products__tooltip--dark'>
                <button
                    type='button'
                    className='manage-products__add-line'
                    data-test-id='manage-products-add-line'
                    aria-label={label}
                    onClick={() => props.vm.addLineItem(props.row.rowId)}
                >
                    +
                </button>
            </Tooltip>
        </td>
    );
};

const CatalogueRow = mobxReact.observer((props: { vm: ManageProductsViewModel; row: IManageProductsSelection }) => {
    const { vm, row } = props;
    const selected = row.checked;
    return (
        <tr
            className={classnames('manage-products__row', { 'manage-products__row--unselected': !selected })}
            data-test-id='manage-products-row'
        >
            <td className='manage-products__col-product'>
                <OECheckbox
                    checked={selected}
                    onChange={() => vm.toggleRow(row.rowId)}
                    label={row.productName}
                />
            </td>
            <AddLineItemCell vm={vm} row={row} />
            <td className='manage-products__col-list-price'><Money value={row.listPrice} /></td>
            <td className='manage-products__col-unit-price'>
                {selected ? (
                    <>
                        <AmountInput
                            value={row.unitPrice}
                            testId='manage-products-unit-price'
                            invalid={vm.hasInvalidUnitPrice(row.rowId)}
                            onChange={raw => vm.setUnitPrice(row.rowId, raw)}
                        />
                        {vm.hasInvalidUnitPrice(row.rowId) && (
                            <span className='manage-products__note-error' data-test-id='manage-products-unit-price-error'>
                                <FormattedMessage {...MANAGE_PRODUCTS_UNIT_PRICE_INVALID_TEXT} />
                            </span>
                        )}
                    </>
                ) : null}
            </td>
            <td className='manage-products__col-note'>
                {selected ? (
                    <>
                        <input
                            type='text'
                            className={classnames('manage-products__input', {
                                'manage-products__input--error': vm.needsNote(row.rowId),
                            })}
                            data-test-id='manage-products-notes'
                            value={row.notes}
                            onChange={event => vm.setNotes(row.rowId, event.target.value)}
                        />
                        {vm.needsNote(row.rowId) && (
                            <span className='manage-products__note-error' data-test-id='manage-products-note-error'>
                                <FormattedMessage {...MANAGE_PRODUCTS_NOTE_REQUIRED_TEXT} />
                            </span>
                        )}
                    </>
                ) : null}
            </td>
            <td className='manage-products__col-qty'>
                {selected ? (
                    <QuantityInput
                        value={row.quantity}
                        onChange={raw => vm.setQuantity(row.rowId, raw)}
                        onBlur={() => vm.commitQuantity(row.rowId)}
                    />
                ) : null}
            </td>
            <td className='manage-products__col-total'>
                {selected ? <Money value={selectionLineTotal(row)} /> : null}
            </td>
        </tr>
    );
});

const Catalogue = mobxReact.observer((props: { vm: ManageProductsViewModel }) => {
    const { vm } = props;
    const intl = useIntl();

    const [searchValue, setSearchValue] = useState(vm.search);
    const debouncedSetSearch = useRef(debounce((term: string) => vm.setSearch(term), SEARCH_DEBOUNCE_MS)).current;
    const onSearchChange = (value: string): void => {
        setSearchValue(value);
        debouncedSetSearch(value);
    };

    const renderBody = (): React.ReactNode => {
        if (vm.isLoading) {
            return (
                <div className='manage-products__state' data-test-id='manage-products-loading'>
                    <MaterialSpinner radius={40} color='#1a1a1a' stroke={3} visible={true} />
                    <span><FormattedMessage {...MANAGE_PRODUCTS_LOADING_TEXT} /></span>
                </div>
            );
        }
        if (vm.hasError) {
            return (
                <div className='manage-products__state' data-test-id='manage-products-error'>
                    <p><FormattedMessage {...MANAGE_PRODUCTS_ERROR_TEXT} /></p>
                    <DefaultButton
                        modifier={['slim']}
                        dataTestId='manage-products-retry'
                        text={intl.formatMessage(MANAGE_PRODUCTS_RETRY_TEXT)}
                        onClick={vm.load}
                    />
                </div>
            );
        }
        if (vm.isCatalogueEmpty) {
            return (
                <p className='manage-products__state' data-test-id='manage-products-empty'>
                    <FormattedMessage {...MANAGE_PRODUCTS_CATALOGUE_EMPTY_TEXT} />
                </p>
            );
        }
        return (
            <table className='manage-products__table'>
                <thead>
                    <tr>
                        <th className='manage-products__col-product'>{intl.formatMessage(PRODUCT_COLUMNS.product)}</th>
                        <th className='manage-products__col-add' />
                        <th className='manage-products__col-list-price'>{intl.formatMessage(PRODUCT_COLUMNS.listPrice)}</th>
                        <th className='manage-products__col-unit-price'>{intl.formatMessage(PRODUCT_COLUMNS.unitPrice)}</th>
                        <th className='manage-products__col-note'>{intl.formatMessage(PRODUCT_COLUMNS.notes)}</th>
                        <th className='manage-products__col-qty'>{intl.formatMessage(PRODUCT_COLUMNS.quantity)}</th>
                        <th className='manage-products__col-total'>{intl.formatMessage(PRODUCT_COLUMNS.total)}</th>
                    </tr>
                </thead>
                <tbody>
                    {vm.visibleRows.map(row => (
                        <CatalogueRow vm={vm} row={row} key={row.rowId} />
                    ))}
                </tbody>
            </table>
        );
    };

    return (
        <section className='manage-products__right' data-test-id='manage-products-catalogue'>
            <input
                type='text'
                className='manage-products__search'
                data-test-id='manage-products-search'
                placeholder={intl.formatMessage(MANAGE_PRODUCTS_SEARCH_PLACEHOLDER)}
                value={searchValue}
                onChange={event => onSearchChange(event.target.value)}
            />
            <div className='manage-products__table-wrapper'>
                {renderBody()}
            </div>
        </section>
    );
});

const ManageProductsModal = mobxReact.observer((props: IProps) => {
    const { vm, isOpen, eventName, eventWhenLines } = props;
    const intl = useIntl();

    if (!vm) {
        return null;
    }

    const footer = (
        <div className='manage-products__footer'>
            <DefaultButton
                className='manage-products__cancel'
                modifier={['fat']}
                dataTestId='manage-products-cancel'
                text={intl.formatMessage(CANCEL_TEXT)}
                onClick={vm.requestCancel}
            />
            <DefaultButton
                className='manage-products__apply'
                modifier={['fat', 'confirm-safe']}
                dataTestId='manage-products-apply'
                text={intl.formatMessage(APPLY_TEXT)}
                onClick={vm.apply}
            />
        </div>
    );

    return (
        <CommonModal
            isOpen={isOpen}
            onRequestClose={vm.requestCancel}
            title={intl.formatMessage(MANAGE_PRODUCTS_MODAL_TITLE)}
            footer={footer}
            isWide={true}
            className='manage-products-modal'
        >
            <div className='manage-products'>
                <LeftPanel vm={vm} eventName={eventName} eventWhenLines={eventWhenLines} />
                <Catalogue vm={vm} />
            </div>
        </CommonModal>
    );
});

export { ManageProductsModal };
